import { appConfig } from '../config/env';
import type {
  BridgeMessage,
  BridgePayloadByEvent,
  WidgetChannel,
  WidgetEventType,
} from '../types/bridge';
import { parseWidgetChannel } from './channel';

export { parseWidgetChannel } from './channel';

/**
 * Resolve the parent frame's origin to use as the postMessage target.
 *
 * Priority (proposal §5.3 — all postMessage checked against expected origin):
 *  1. appConfig.panelOrigin is the baked-in panel host; the loader lives on
 *     the same origin as the panel, so the parent frame is that same host when
 *     embedded normally. Use it as the authoritative target.
 *  2. Fall back to document.referrer when available (handles edge cases where
 *     the panel is embedded from a different path on the same host).
 *  3. Last resort: '*' — only reached if neither is available (e.g. direct
 *     navigation to the panel URL in a browser tab during development).
 */
function resolveParentTargetOrigin(): string {
  // Use baked-in panel origin — the loader and panel share the same host.
  try {
    const panelOrigin = new URL(appConfig.panelOrigin).origin;
    if (panelOrigin) return panelOrigin;
  } catch {
    // malformed panelOrigin — fall through
  }
  // Referrer as secondary source of truth
  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // ignore malformed referrer
  }
  // Development-only fallback — postMessage to '*' is safe here because
  // development builds never handle sensitive data over this bridge.
  return '*';
}

type NativeHostWindow = Window & {
  webkit?: {
    messageHandlers?: {
      avatarHost?: { postMessage: (message: unknown) => void };
    };
  };
  AvatarNative?: { postMessage: (message: string) => void };
};

function postToNativeHost(message: BridgeMessage): void {
  const json = JSON.stringify(message);
  const nativeWindow = window as NativeHostWindow;
  try {
    nativeWindow.webkit?.messageHandlers?.avatarHost?.postMessage(json);
  } catch {
    // Host has no WKScriptMessageHandler — ignore (browser / iframe).
  }
  try {
    nativeWindow.AvatarNative?.postMessage(json);
  } catch {
    // Host has no JavascriptInterface — ignore.
  }
}

export function postToHost<E extends WidgetEventType>(
  event: E,
  payload: Partial<BridgePayloadByEvent[E]> = {},
): void {
  // Callers pass partial extras; timestamp is always stamped here.
  const message: BridgeMessage = {
    source: 'avatar_core_iframe',
    event,
    payload: {
      timestamp: Date.now(),
      ...payload,
    },
  } as BridgeMessage;
  postToNativeHost(message);
  if (window.parent !== window) {
    window.parent.postMessage(message, resolveParentTargetOrigin());
  }
}

export interface PanelQueryParams {
  channel: WidgetChannel;
  publishableKey: string;
  /** Optional session id from loader (`?session=`) — proposal §5.1.5 */
  sessionId: string;
  /** iOS bundle id / Android package from the native SDK (`?bundle=`). */
  bundleId: string;
}

export function readQueryParams(): PanelQueryParams {
  const params = new URLSearchParams(window.location.search);
  const channel = parseWidgetChannel(params.get('surface'));
  // Embedded panels always get ?pk=; the fallback is so a bare
  // http://localhost:5173 still loads config in dev (empty elsewhere).
  const publishableKey = params.get('pk') || appConfig.devPublishableKey;
  return {
    channel,
    publishableKey,
    sessionId: params.get('session') || '',
    bundleId: (params.get('bundle') || '').trim(),
  };
}

export function resolvePublishableKey(payload: {
  publishableKey?: string;
  publicKey?: string;
}): string {
  return payload.publishableKey || payload.publicKey || '';
}
